import React from 'react'
import PropTypes from 'prop-types'
import styles from './styles'
import {
  getInitialButtonState,
  getLoaderColour,
  canRenderIcon,
  isCircularButton,
  getButtonStyle,
  getIconStyle,
  getIconLineWeight,
  getContainerStyle,
  getButtonComponentType
} from './helpers'
import { ICON_COMPONENTS } from './constants'
import Loader from './children/button_loader'

class Button extends React.Component {
  constructor (props) {
    super(props)
    this.styles = styles(props)
    this.state = getInitialButtonState()
    this.bindHandlers()
  }

  bindHandlers () {
    this.handleMouseOver = this.handleMouseOver.bind(this)
    this.handleMouseOut = this.handleMouseOut.bind(this)
    this.handleMouseDown = this.handleMouseDown.bind(this)
    this.handleMouseUp = this.handleMouseUp.bind(this)
    this.handleFocus = this.handleFocus.bind(this)
    this.handleBlur = this.handleBlur.bind(this)
    this.handleClick = this.handleClick.bind(this)
  }

  componentWillReceiveProps (nextProps) {
    this.styles = styles(nextProps)
  }

  componentDidUpdate (prevProps) {
    if (this.props.disabled !== prevProps.disabled) {
      this.setState(getInitialButtonState())
    }
  }

  handleMouseOver () {
    this.setState({ mouseOver: true })
  }

  handleMouseOut () {
    this.setState({
      mouseOver: false,
      mouseDown: false
    })
  }

  handleMouseDown () {
    this.setState({ mouseDown: true })
  }

  handleMouseUp () {
    this.setState({ mouseDown: false })
  }

  handleFocus () {
    this.setState({ focused: true })
  }

  handleBlur () {
    this.setState({ focused: false })
  }

  handleClick (e) {
    if (this.props.isMobile) {
      this.setState({ focused: false })
      this.setState({ mouseOver: false })
    }
    this.props.onClick(e)
  }

  renderIcon (location) {
    if (!canRenderIcon(this.props, location)) return

    const IconComponent = ICON_COMPONENTS[this.props.icon]
    const iconStyle = getIconStyle(this.props, this.state, this.styles)
    return (
      <IconComponent
        style={iconStyle}
        colour={iconStyle.color}
        height={iconStyle.height}
        lineWeight={getIconLineWeight(this.props)}
        rotateDegrees={this.props.iconRotateDegrees}
      />
    )
  }

  renderText () {
    return isCircularButton(this.props) ? null : (
      <span>{this.props.text}</span>
    )
  }

  renderContents () {
    if (this.props.loading) return <Loader colour={getLoaderColour(this.props)} />
    return (
      <React.Fragment>
        {this.renderIcon('left')}
        {this.renderText()}
        {this.renderIcon('right')}
      </React.Fragment>
    )
  }

  render () {
    const ButtonComponent = getButtonComponentType(this.props)
    return (
      <div
        style={getContainerStyle(this.props, this.styles)}
        onMouseOver={this.handleMouseOver}
        onMouseOut={this.handleMouseOut}
        onMouseDown={this.handleMouseDown}
        onMouseUp={this.handleMouseUp}
      >
        <ButtonComponent
          href={this.props.href}
          type={this.props.buttonType}
          disabled={this.props.disabled}
          onClick={this.handleClick}
          style={getButtonStyle(this.props, this.state, this.styles)}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
        >
          {this.renderContents()}
        </ButtonComponent>
      </div>
    )
  }
}

Button.defaultProps = {
  onClick: () => {},
  disabled: false,
  inline: true,
  fullWidth: false,
  text: '',
  type: 'alpha',
  colour: 'uiBlue',
  size: 'medium',
  hoverType: 'tone',
  underlined: false,
  iconLocation: 'left',
  isMobile: false,
  href: null,
  align: 'center',
  loading: false
}

Button.propTypes = {
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  inline: PropTypes.bool,
  fullWidth: PropTypes.bool,
  text: PropTypes.string,
  underlined: PropTypes.bool,
  buttonType: PropTypes.oneOf(['submit', 'reset', 'button']),
  type: PropTypes.oneOf(['alpha', 'bravo', 'charlie', 'delta', 'circular']),
  colour: PropTypes.oneOf(['uiBlue', 'wildBlackberry', 'sourCherry', 'bubblegum', 'margarita', 'berryBlue', 'huckleberry']),
  size: PropTypes.oneOf(['small', 'medium', 'big', 'large']),
  hoverType: PropTypes.oneOf(['tone', 'shadow']),
  icon: PropTypes.oneOf(['location', 'pencil', 'add', 'logout', 'arrowShort']),
  iconProps: PropTypes.object,
  iconLocation: PropTypes.oneOf(['left', 'right']),
  iconRotateDegrees: PropTypes.oneOf([0, 90, 180, 270]),
  isMobile: PropTypes.bool,
  href: PropTypes.string,
  align: PropTypes.oneOf(['left', 'center', 'right']),
  loading: PropTypes.bool
}

export default Button
